// API for dialer subscription management
// Powered by Supabase

import { supabase } from '../lib/supabase';
import { createNotification } from './notifications';

async function sendDialerCredentialsEmail(subscriptionId: string, isUpdate: boolean) {
  try {
    const { error } = await supabase.functions.invoke('send-dialer-credentials', {
      body: { subscriptionId, isUpdate },
    });
    if (error) console.error('Dialer credentials email failed (non-critical):', error);
  } catch (err) {
    console.error('Dialer credentials email error (non-critical):', err);
  }
}

// Product constants
export const DIALER_PRODUCT_IDS = [7, 16, 17, 19, 25, 18] as const;

export const DEFAULT_DURATIONS: Record<number, number> = {
  18: 30, // V.0: 30 days
  7: 30,  // V.1: 30 days
  16: 30, // V.2: 30 days
  17: 7,  // V.3 IUL: 7 days
  19: 30, // V.4 MI: 30 days
  25: 30, // November Missed Inbound: 30 days (monthly enrollment product)
};

export const DIALER_PRODUCT_NAMES: Record<number, string> = {
  18: 'Dialer Seat V.0',
  7: 'Dialer Seat V.1',
  16: 'Dialer Seat V.2',
  17: 'Dialer Seat V.3 (IUL)',
  19: 'Dialer Seat V.4 (MI)',
  25: 'November Missed Inbound Dialer',
};

// Name-based classifier. Historically "missed inbound" would resolve to
// V.4 MI (19). The monthly-enrollment inbound dialer (April/May/June/July/August/September/October/November)
// uses id 25 and must be classified separately so the admin can track it.
export function resolveDialerProductId(productId: number | null | undefined, productName: string | null | undefined): number {
  if (productId && productId !== 0) return productId;
  const n = (productName ?? '').toLowerCase();
  if (n.includes('april missed inbound') || n.includes('may missed inbound') || n.includes('june missed inbound') || n.includes('july missed inbound') || n.includes('august missed inbound') || n.includes('september missed inbound') || n.includes('october missed inbound') || n.includes('november missed inbound')) return 25;
  if (n.includes('v.0') || n.includes('v0')) return 18;
  if (n.includes('v.2') || n.includes('v2')) return 16;
  if (n.includes('v.3') || n.includes('iul')) return 17;
  if (n.includes('v.4') || (n.includes('missed inbound') && !n.includes('may') && !n.includes('april') && !n.includes('june') && !n.includes('july') && !n.includes('august') && !n.includes('september') && !n.includes('october') && !n.includes('november'))) return 19;
  if (n.includes('v.1') || n.includes('v1')) return 7;
  return 7;
}

// Unique fingerprints stamped into the order's `notes` field by the
// Missed Inbound Dialer product page (AprilMissedInboundPage.tsx ships
// `notes: "1 Month Access - November | Open Enrollment"` on every checkout).
// No other code path produces these strings, so matching them guarantees
// the subscription came from a real checkout of THIS specific product.
// November is its own enrollment month: we match ONLY the November marker so the
// admin's November views never include the prior October cycle's purchasers. (The
// October marker was matched here during the prior cycle and is intentionally
// moved to the prior-cycle classifier below now that November is current.)
const MISSED_INBOUND_ORDER_NOTE_MARKERS = [
  '1 month access - november',
];

// November open enrollment started October 25 (midnight Eastern), before the
// product page rolled, so orders from that date on can still carry the October
// marker. They are November purchasers and count in the November bucket.
const CURRENT_CYCLE_ENROLLMENT_START = Date.parse('2026-10-25T04:00:00Z');

function isLateOctoberMarkedEnrollment(sub: {
  order_notes?: string | null;
  created_at?: string | null;
}): boolean {
  const notes = (sub.order_notes ?? '').toLowerCase();
  if (!OCTOBER_MISSED_INBOUND_ORDER_NOTE_MARKERS.some(marker => notes.includes(marker))) return false;
  const created = Date.parse(sub.created_at ?? '');
  return Number.isFinite(created) && created >= CURRENT_CYCLE_ENROLLMENT_START;
}

// True if this subscription is a REAL customer purchase of the monthly
// Missed Inbound Dialer made via the public checkout flow. We
// authenticate the row by its linked order's notes fingerprint rather
// than by product_name, because product_name can be mutated by the
// admin "edit subscription" flow (which is how legacy subs like
// Oliver's were leaking in).
export function isMissedInboundDialerSub(sub: {
  order_id?: string | null;
  order_notes?: string | null;
  created_at?: string | null;
}): boolean {
  if (!sub.order_id) return false;
  const notes = (sub.order_notes ?? '').toLowerCase();
  return MISSED_INBOUND_ORDER_NOTE_MARKERS.some(marker => notes.includes(marker))
    || isLateOctoberMarkedEnrollment(sub);
}

// Prior enrollment cycle (October), tracked as its own bucket so the admin can
// see the winding-down October purchasers separately from November. October checkouts
// stamped the hyphen variant into the order notes.
const OCTOBER_MISSED_INBOUND_ORDER_NOTE_MARKERS = [
  '1 month access - october',
];

// True if this subscription is a REAL October Missed Inbound Dialer purchase.
// Same order-notes authentication as the November classifier, matched against
// the October marker. The admin's October bucket additionally hides expired subs,
// so the bucket drains itself as each October enrollment runs out.
export function isOctoberMissedInboundDialerSub(sub: {
  order_id?: string | null;
  order_notes?: string | null;
  created_at?: string | null;
}): boolean {
  if (!sub.order_id) return false;
  const notes = (sub.order_notes ?? '').toLowerCase();
  return OCTOBER_MISSED_INBOUND_ORDER_NOTE_MARKERS.some(marker => notes.includes(marker))
    && !isLateOctoberMarkedEnrollment(sub);
}

export const DIALER_PRODUCT_PRICES: Record<number, number> = {
  18: 250,
  7: 500,
  16: 1000,
  17: 500,
  19: 1000,
  25: 500,
};

export type DialerSubscriptionStatus = 'pending' | 'active' | 'expiring_soon' | 'expired';

export interface DialerSubscription {
  id: string;
  order_id: string | null;
  agent_id: string;
  product_id: number;
  product_name: string;
  product_price: number | null;
  dialer_username: string | null;
  dialer_password: string | null;
  duration_days: number;
  started_at: string | null;
  expires_at: string | null;
  status: DialerSubscriptionStatus;
  is_free: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DialerSubWithAgent extends DialerSubscription {
  agent_name?: string;
  agent_email?: string;
  states?: string;
  order_notes?: string;
}

// Admin: fetch all subscriptions with agent info
export async function getAllDialerSubscriptions(): Promise<{ success: boolean; data?: DialerSubWithAgent[]; error?: string }> {
  try {
    const { data: subs, error } = await supabase
      .from('dialer_subscriptions')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;

    // Fetch agent profiles for names/emails
    const agentIds = [...new Set((subs ?? []).map((s: any) => s.agent_id))];
    if (agentIds.length === 0) return { success: true, data: (subs ?? []) as DialerSubWithAgent[] };

    // Batched for the same URL-length reason as the orders lookup below.
    const agents: { user_id: string; first_name: string | null; last_name: string | null; email: string | null }[] = [];
    for (let i = 0; i < agentIds.length; i += 100) {
      const { data: batch, error: agentsError } = await supabase
        .from('agents')
        .select('user_id, first_name, last_name, email')
        .in('user_id', agentIds.slice(i, i + 100));
      if (agentsError) throw agentsError;
      agents.push(...(batch ?? []));
    }

    const agentMap = new Map(
      (agents ?? []).map((a: any) => [a.user_id, { name: `${a.first_name} ${a.last_name}`.trim(), email: a.email }])
    );

    // Fetch linked orders for unpaid filtering, fallback name/email, AND states
    const orderIds = [...new Set((subs ?? []).filter((s: any) => s.order_id).map((s: any) => s.order_id))];
    const unpaidOrderIds = new Set<string>();
    const orderMap = new Map<string, { name: string; email: string; states: string; notes: string }>();
    // Fetched in batches: one request with every order id makes the URL too
    // long and the request fails, which silently dropped all order data
    // (unpaid checkouts showed as pending, enrollment notes went missing).
    // A failed batch throws so the admin sees an error, not wrong numbers.
    const ORDER_BATCH_SIZE = 100;
    for (let i = 0; i < orderIds.length; i += ORDER_BATCH_SIZE) {
      const { data: linkedOrders, error: ordersError } = await supabase
        .from('orders')
        .select('id, status, customer_name, customer_email, states, notes')
        .in('id', orderIds.slice(i, i + ORDER_BATCH_SIZE));
      if (ordersError) throw ordersError;
      (linkedOrders ?? []).forEach((o: any) => {
        if (o.status !== 'completed') unpaidOrderIds.add(o.id);
        orderMap.set(o.id, {
          name: o.customer_name ?? '',
          email: o.customer_email ?? '',
          states: o.states ?? '',
          notes: o.notes ?? '',
        });
      });
    }

    const result = (subs ?? [])
      .filter((s: any) => !s.order_id || !unpaidOrderIds.has(s.order_id))
      .map((s: any) => {
        const agent = agentMap.get(s.agent_id);
        const order = s.order_id ? orderMap.get(s.order_id) : null;
        return {
          ...s,
          agent_name: agent?.name || order?.name || 'Unknown',
          agent_email: agent?.email || order?.email || '',
          states: order?.states || '',
          order_notes: order?.notes || '',
        };
      }) as DialerSubWithAgent[];

    return { success: true, data: result };
  } catch (error: any) {
    console.error('Error fetching dialer subscriptions:', error);
    return { success: false, error: error.message ?? 'Failed to fetch subscriptions' };
  }
}

// Agent: fetch own subscriptions
export async function getAgentDialerSubscriptions(agentId: string): Promise<{ success: boolean; data?: DialerSubscription[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('dialer_subscriptions')
      .select('*')
      .eq('agent_id', agentId)
      .order('created_at', { ascending: false });
    if (error) throw error;

    // Filter out subs linked to unpaid/pending orders
    const orderIds = [...new Set((data ?? []).filter((s: any) => s.order_id).map((s: any) => s.order_id))];
    let unpaidOrderIds = new Set<string>();
    if (orderIds.length > 0) {
      const { data: unpaidOrders } = await supabase
        .from('orders')
        .select('id')
        .in('id', orderIds)
        .neq('status', 'completed');
      (unpaidOrders ?? []).forEach((o: any) => unpaidOrderIds.add(o.id));
    }

    const filtered = (data ?? []).filter((s: any) => !s.order_id || !unpaidOrderIds.has(s.order_id));
    return { success: true, data: filtered as DialerSubscription[] };
  } catch (error: any) {
    console.error('Error fetching agent subscriptions:', error);
    return { success: false, error: error.message ?? 'Failed to fetch subscriptions' };
  }
}

// Create a new subscription (admin only)
export async function createDialerSubscription(params: {
  agent_id: string;
  product_id: number;
  product_name: string;
  product_price: number;
  duration_days: number;
  notes?: string;
  is_free?: boolean;
  is_unlimited?: boolean;
  custom_started_at?: string;
  custom_expires_at?: string;
  custom_created_at?: string;
}): Promise<{ success: boolean; data?: DialerSubscription; error?: string }> {
  try {
    const unlimited = params.is_unlimited || params.duration_days === -1;
    const insertData: Record<string, any> = {
      agent_id: params.agent_id,
      product_id: params.product_id,
      product_name: params.product_name,
      product_price: params.is_free ? 0 : params.product_price,
      duration_days: unlimited ? -1 : params.duration_days,
      notes: params.notes ?? null,
      is_free: params.is_free ?? false,
      status: 'pending' as DialerSubscriptionStatus,
    };

    if (params.custom_created_at) {
      insertData.created_at = params.custom_created_at;
    }
    if (params.custom_started_at) {
      insertData.started_at = params.custom_started_at;
      insertData.status = 'active';
    }
    if (unlimited) {
      insertData.expires_at = null;
      if (params.custom_started_at) insertData.status = 'active';
    } else if (params.custom_expires_at) {
      insertData.expires_at = params.custom_expires_at;
      if (new Date(params.custom_expires_at) < new Date()) {
        insertData.status = 'expired';
      }
    }

    const { data, error } = await supabase
      .from('dialer_subscriptions')
      .insert(insertData)
      .select()
      .single();
    if (error) throw error;
    return { success: true, data: data as DialerSubscription };
  } catch (error: any) {
    console.error('Error creating subscription:', error);
    return { success: false, error: error.message ?? 'Failed to create subscription' };
  }
}

// Start campaign: set credentials, start timer
export async function startCampaign(params: {
  subscriptionId: string;
  dialerUsername: string;
  dialerPassword: string;
  durationDays?: number;
  customStartDate?: string;
}): Promise<{ success: boolean; data?: DialerSubscription; error?: string }> {
  try {
    const { data: current, error: fetchErr } = await supabase
      .from('dialer_subscriptions')
      .select('*')
      .eq('id', params.subscriptionId)
      .single();
    if (fetchErr) throw fetchErr;

    const days = params.durationDays ?? (current as DialerSubscription).duration_days;
    const unlimited = days === -1;
    const startDate = params.customStartDate ? new Date(params.customStartDate) : new Date();
    const expiresAt = unlimited ? null : new Date(startDate.getTime() + days * 24 * 60 * 60 * 1000);

    const { data, error } = await supabase
      .from('dialer_subscriptions')
      .update({
        dialer_username: params.dialerUsername,
        dialer_password: params.dialerPassword,
        duration_days: days,
        started_at: startDate.toISOString(),
        expires_at: expiresAt?.toISOString() ?? null,
        status: 'active' as DialerSubscriptionStatus,
      })
      .eq('id', params.subscriptionId)
      .select()
      .single();
    if (error) throw error;

    const sub = data as DialerSubscription;
    createNotification(
      sub.agent_id,
      'dialer_credentials_ready',
      'Dialer Credentials Ready',
      `Your ${sub.product_name} subscription is now active. View your credentials on the My Dialer page.`,
      { subscription_id: sub.id }
    );

    sendDialerCredentialsEmail(sub.id, false);

    return { success: true, data: sub };
  } catch (error: any) {
    console.error('Error starting campaign:', error);
    return { success: false, error: error.message ?? 'Failed to start campaign' };
  }
}

// Update dates on an existing subscription (admin override)
export async function updateSubscriptionDates(
  subscriptionId: string,
  startedAt: string | null,
  expiresAt: string | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const updates: Record<string, any> = {};
    if (startedAt !== undefined) updates.started_at = startedAt;
    if (expiresAt !== undefined) updates.expires_at = expiresAt;

    if (expiresAt) {
      const exp = new Date(expiresAt);
      const now = new Date();
      const fiveDays = new Date(now.getTime() + 5 * 86400000);
      if (exp < now) updates.status = 'expired';
      else if (exp < fiveDays) updates.status = 'expiring_soon';
      else updates.status = 'active';
    }

    const { error } = await supabase
      .from('dialer_subscriptions')
      .update(updates)
      .eq('id', subscriptionId);
    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error updating subscription dates:', error);
    return { success: false, error: error.message ?? 'Failed to update dates' };
  }
}

// Update credentials only (does NOT reset timer, but activates pending subs)
export async function updateCredentials(
  subscriptionId: string,
  username: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: current, error: fetchErr } = await supabase
      .from('dialer_subscriptions')
      .select('status, started_at, agent_id, product_name')
      .eq('id', subscriptionId)
      .single();
    if (fetchErr) throw fetchErr;

    const updates: Record<string, any> = {
      dialer_username: username,
      dialer_password: password,
    };

    if (current.status === 'pending') {
      updates.status = 'active';
      if (!current.started_at) {
        updates.started_at = new Date().toISOString();
      }
    }

    const { error } = await supabase
      .from('dialer_subscriptions')
      .update(updates)
      .eq('id', subscriptionId);
    if (error) throw error;

    if (current.status === 'pending') {
      createNotification(
        current.agent_id,
        'dialer_credentials_ready',
        'Dialer Credentials Ready',
        `Your ${current.product_name} subscription is now active. View your credentials on the My Dialer page.`,
        { subscription_id: subscriptionId }
      );
    }

    sendDialerCredentialsEmail(subscriptionId, true);

    return { success: true };
  } catch (error: any) {
    console.error('Error updating credentials:', error);
    return { success: false, error: error.message ?? 'Failed to update credentials' };
  }
}

// Manually expire a subscription
export async function expireSubscription(subscriptionId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('dialer_subscriptions')
      .update({
        status: 'expired' as DialerSubscriptionStatus,
        dialer_username: null,
        dialer_password: null,
      })
      .eq('id', subscriptionId);
    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error expiring subscription:', error);
    return { success: false, error: error.message ?? 'Failed to expire subscription' };
  }
}

// Renew / extend a subscription
export async function renewSubscription(
  subscriptionId: string,
  additionalDays: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: current, error: fetchErr } = await supabase
      .from('dialer_subscriptions')
      .select('*')
      .eq('id', subscriptionId)
      .single();
    if (fetchErr) throw fetchErr;

    const sub = current as DialerSubscription;
    const baseDate = sub.expires_at ? new Date(sub.expires_at) : new Date();
    const newExpiry = new Date(baseDate.getTime() + additionalDays * 24 * 60 * 60 * 1000);

    const { error } = await supabase
      .from('dialer_subscriptions')
      .update({
        expires_at: newExpiry.toISOString(),
        status: 'active' as DialerSubscriptionStatus,
        duration_days: sub.duration_days + additionalDays,
      })
      .eq('id', subscriptionId);
    if (error) throw error;

    createNotification(
      sub.agent_id,
      'dialer_subscription_renewed',
      'Subscription Renewed',
      `Your ${sub.product_name} subscription has been renewed for ${additionalDays} more days.`,
      { subscription_id: subscriptionId }
    );

    return { success: true };
  } catch (error: any) {
    console.error('Error renewing subscription:', error);
    return { success: false, error: error.message ?? 'Failed to renew subscription' };
  }
}

// Switch product on an existing subscription
export async function updateSubscriptionProduct(
  subscriptionId: string,
  productId: number,
  productName: string,
  productPrice: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('dialer_subscriptions')
      .update({
        product_id: productId,
        product_name: productName,
        product_price: productPrice,
      })
      .eq('id', subscriptionId);
    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error updating subscription product:', error);
    return { success: false, error: error.message ?? 'Failed to update product' };
  }
}

// Delete a subscription (admin only, typically for expired subs)
export async function deleteSubscription(subscriptionId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('dialer_subscriptions')
      .delete()
      .eq('id', subscriptionId);
    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting subscription:', error);
    return { success: false, error: error.message ?? 'Failed to delete subscription' };
  }
}

// Check and update expirations (call periodically)
export async function checkExpirations(): Promise<void> {
  const now = new Date().toISOString();
  const fiveDaysFromNow = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString();

  // Mark expired + scrub credentials (skip unlimited subs where expires_at is null)
  await supabase
    .from('dialer_subscriptions')
    .update({
      status: 'expired' as DialerSubscriptionStatus,
      dialer_username: null,
      dialer_password: null,
    })
    .in('status', ['active', 'expiring_soon'])
    .not('expires_at', 'is', null)
    .lt('expires_at', now);

  // Mark expiring_soon (within 5 days), skip unlimited
  await supabase
    .from('dialer_subscriptions')
    .update({ status: 'expiring_soon' as DialerSubscriptionStatus })
    .eq('status', 'active')
    .not('expires_at', 'is', null)
    .lt('expires_at', fiveDaysFromNow)
    .gte('expires_at', now);
}

// Helper: days remaining
export function getDaysRemaining(expiresAt: string | null): number {
  if (!expiresAt) return 0;
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

// Helper: progress percentage (for countdown bar)
export function getProgressPercent(startedAt: string | null, expiresAt: string | null): number {
  if (!startedAt || !expiresAt) return 0;
  const start = new Date(startedAt).getTime();
  const end = new Date(expiresAt).getTime();
  const now = Date.now();
  const total = end - start;
  if (total <= 0) return 0;
  const elapsed = now - start;
  const remaining = Math.max(0, 1 - elapsed / total);
  return Math.round(remaining * 100);
}

// Compute aggregate stats
export interface DialerStats {
  active: number;
  pending: number;
  expiringSoon: number;
  expired: number;
  monthlyRevenue: number;
  byProduct: { product: string; count: number }[];
  byStatus: { status: string; count: number }[];
}

export function computeStats(subs: DialerSubWithAgent[]): DialerStats {
  const active = subs.filter((s) => s.status === 'active').length;
  const pending = subs.filter((s) => s.status === 'pending').length;
  const expiringSoon = subs.filter((s) => s.status === 'expiring_soon').length;
  const expired = subs.filter((s) => s.status === 'expired').length;

  const monthlyRevenue = subs
    .filter((s) => (s.status === 'active' || s.status === 'expiring_soon') && !s.is_free)
    .reduce((sum, s) => sum + (s.product_price ?? 0), 0);

  const productCounts = new Map<number, number>();
  subs
    .filter((s) => s.status === 'active' || s.status === 'expiring_soon' || s.status === 'pending')
    .forEach((s) => {
      const normalizedId = resolveDialerProductId(s.product_id, s.product_name);
      productCounts.set(normalizedId, (productCounts.get(normalizedId) ?? 0) + 1);
    });
  const byProduct = Array.from(productCounts.entries()).map(([productId, count]) => ({
    product: DIALER_PRODUCT_NAMES[productId] ?? `Product #${productId}`,
    count,
  }));

  const statusCounts = new Map<string, number>();
  subs.forEach((s) => {
    statusCounts.set(s.status, (statusCounts.get(s.status) ?? 0) + 1);
  });
  const byStatus = Array.from(statusCounts.entries()).map(([status, count]) => ({ status, count }));

  return { active, pending, expiringSoon, expired, monthlyRevenue, byProduct, byStatus };
}
